import { useMemo } from 'react'
import { useUsage } from '~/hooks'
import {
  DYNAMIC_QUOTAS,
  QUOTA_TYPE_USER_FACING_PROPERTIES_MAP,
  QuotaUsedPercentageWarningThreshold,
} from '../../usage/constants'
import { match } from 'ts-pattern'
import { Link } from '~/elements'
import { formatValueWithUnit } from '../../usage/helpers'
import { CircleAlertIcon, TriangleAlertIcon } from 'lucide-react'
import { AlertTitle, ColoredAlert } from '@bpinternal/ui-kit-next'

type Props = {
  workspaceId: string
  className?: string
}

export const WorkspaceUsageCallout = ({ workspaceId, className }: Props) => {
  const usages = useUsage({
    workspaceId,
    quotas: DYNAMIC_QUOTAS,
  })

  const [highestUsage] = useMemo(() => {
    return Object.values(usages)
      .map(({ value, quota, ...rest }) => ({
        usage: value,
        quota,
        ...rest,
      }))
      .filter(({ percentage }) => percentage > QuotaUsedPercentageWarningThreshold)
      .sort((a, b) => b.percentage - a.percentage)
  }, [usages])

  return match(highestUsage)
    .with(undefined, () => null)
    .when(
      ({ percentage }) => percentage >= 100,
      ({ quota, type }) => (
        <ColoredAlert color="red" className={className}>
          <CircleAlertIcon className="size-5" />
          <AlertTitle className="mb-0 text-sm">
            You've used 100% of your {formatValueWithUnit(quota, type)}{' '}
            {QUOTA_TYPE_USER_FACING_PROPERTIES_MAP[type].name}. To restore functionality and prevent disruption, we
            recommend <BillingActionLinks type={type} workspaceId={workspaceId} linkColor="red" />
          </AlertTitle>
        </ColoredAlert>
      )
    )
    .when(
      ({ percentage }) => percentage >= 75,
      ({ quota, type, percentage }) => (
        <ColoredAlert color="amber" className={className}>
          <TriangleAlertIcon className="size-5" />
          <AlertTitle className="mb-0 text-sm">
            You have used {percentage.toFixed(0)}% of your {formatValueWithUnit(quota, type)} limit for{' '}
            {QUOTA_TYPE_USER_FACING_PROPERTIES_MAP[type].name}. To avoid service disruption, consider{' '}
            <BillingActionLinks type={type} workspaceId={workspaceId} linkColor="amber" />
            to increase your limit.
          </AlertTitle>
        </ColoredAlert>
      )
    )
    .otherwise(() => null)
}

const BillingActionLinks = ({
  type,
  workspaceId,
  linkColor,
}: {
  type: string
  workspaceId: string
  linkColor: 'amber' | 'red'
}) => {
  const linkClassName =
    linkColor === 'amber'
      ? 'text-amber-800 underline decoration-amber-800'
      : 'text-red-800 underline decoration-red-800'

  if (type === 'ai_spend' || type === 'openai_spend') {
    return (
      <Link className={linkClassName} to="/workspaces/$workspaceId/billing" params={{ workspaceId }}>
        raising your AI spend{' '}
      </Link>
    )
  }

  return (
    <>
      <Link className={linkClassName} to="/workspaces/$workspaceId/billing" params={{ workspaceId }}>
        {' '}
        upgrading your plan
      </Link>{' '}
      or{' '}
      <Link className={linkClassName} to="/workspaces/$workspaceId/billing" params={{ workspaceId }}>
        adding an add-on
      </Link>{' '}
    </>
  )
}
